// Single source of truth: the compensated_by and compensation_method vocabularies live in the compensation model.
import type { CompensatedBy, CompensationMethod } from './compensation';
import type { Lab, PaymentBillingType } from './lab';
import type { ResourceType } from './role';

export const tradeSellTypes = ['sellin', 'sellout', 'sellin_and_sellout'] as const;

export type TradeSellType = (typeof tradeSellTypes)[number];

export type TradeStatus = 'created' | 'closed';

export type TradeKind =
  | 'group_owned'
  | 'direct'
  | 'platform'
  | 'generic'
  | 'prevalidate'
  | 'platform_connect';

export type TradeRequirementKind = 'picture' | 'sellout';

export const tradeTargeting = [
  'master_pharmacy_list',
  'pharmacy_list',
  'group_all',
  'group_none',
  'pharmacy_owner',
] as const;

export const PICTURE_SELECTABLE_COMPENSATION_TYPE_OPTIONS = [
  'euros_fixed',
  'rfa_percentage',
  'other',
  'none',
] as const;
export const SELLOUT_SELECTABLE_COMPENSATION_TYPE_OPTIONS = [
  'percentage',
  'bri',
  'multi_bri',
  'tiered_free_units_with_mixing',
  'tiered_free_units_without_mixing',
  'nth_item_discount_with_mixing',
  'nth_item_discount_without_mixing',
  'other',
  'none',
] as const;
export const SELLOUT_NOT_SELECTABLE_COMPENSATION_TYPE_OPTIONS = [
  'euros_pharmacy_provided',
] as const;

export const COMPENSATION_TYPE_OPTIONS = [
  ...PICTURE_SELECTABLE_COMPENSATION_TYPE_OPTIONS,
  ...SELLOUT_SELECTABLE_COMPENSATION_TYPE_OPTIONS,
  ...SELLOUT_NOT_SELECTABLE_COMPENSATION_TYPE_OPTIONS,
] as const;

export type TradeTargeting = (typeof tradeTargeting)[number];

export type GroupAgreementStatus = 'not_needed' | 'awaiting_group_decision' | 'refused' | 'agreed';
export type CompensationType = (typeof COMPENSATION_TYPE_OPTIONS)[number];

export type PmsPromotionStatus =
  | 'pending_faks'
  | 'pending_pms'
  | 'call_successful'
  | 'call_failure';

export type PmsPromotionErrorCode =
  | 'unknown_products'
  | 'all_products_unknown'
  | 'unknown_pharmacy_identifier'
  | 'invalid_pharmacy_identifier'
  | 'bad_input'
  | 'api_busy'
  | 'no_data'
  | 'pharmacy_offline'
  | 'operation_not_found'
  | 'too_many_products'
  | 'integration_error'
  | 'integration_refused';

export type PmsPromotion = {
  status: PmsPromotionStatus;
  title: string;
  submitted_at: string | null;
  error_code: PmsPromotionErrorCode | null;
  error_details: string | null;
};

export interface TradeRequirement {
  id: number;
  kind: TradeRequirementKind;
  name: string;
  description: string | null;
  start_date: string;
  end_date: string;
  trade_id: number;
  compensation_type: CompensationType | null;
  compensation_amount_in_euros_cents: number | null;
  compensation_permyriad: number | null;
  compensation_ug_free_quantity: number | null;
  compensation_ug_threshold: number | null;
  compensation_method: CompensationMethod | null;
  compensated_by: CompensatedBy | null;
  compensation_type_other_text: string | null;
}

export interface TradeProduct {
  id: number;
  ean: string;
  name: string;
  vat_rate_in_percent: number | null;
  compensation_in_cents: number | null;
  recommended_price_in_cents: number | null;
}

export type PharmacyAccountingDocumentToGenerate =
  | 'none'
  | 'invoice_service_agreement'
  | 'invoice_account_rendered';

export interface Trade {
  id: number;
  sell_type: TradeSellType;
  kind: TradeKind;
  archived: boolean;
  title: string;
  description: string;
  status: TradeStatus;
  owner_type: ResourceType;
  owner_id: number;
  start_date: string;
  end_date: string;
  visible: boolean;
  lab_trade_id: number | null;
  mandate_contract_url: string | null;
  has_group_priority: boolean;
  has_group_pre_subscription: boolean;
  created_at: string;
  targeting: TradeTargeting;
  customer_promotion: string | null;
  customer_promotion_details: string | null;
  hidden_from_group: boolean;
  faks_payment_enabled: boolean;
  out_of_group_agreement: boolean;
  group_agreement: GroupAgreementStatus;
  group_collab_refusal_reason: string | null;
  can_group_refuse_trade: boolean;
  can_group_edit_lab_sub_trade: boolean;
  can_group_mark_as_out_of_agreement: boolean;
  lab_premium_option_enabled: boolean;
  additional_description: string | null;
  platform_connect_url: string | null;
  participation_window_extended_until: string | null;
  external_id: string | null;
  pharmacy_accounting_document_to_generate: PharmacyAccountingDocumentToGenerate;
  group_collab_modification_requests: {
    message: string;
    requested_at: string;
  }[];
  group_collab_messages: {
    message: string;
    sent_at: string;
  }[];
}

export type TradeTag = {
  id: number;
  name: string;
  group_id: number;
};

export type TradeLab = Pick<Lab, 'id' | 'logo_url' | 'name'> & {
  trade_message: string | null;
  payment_and_billing_type: PaymentBillingType;
};
